import { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { BarChart } from "@mui/x-charts";
import apiService from "../../../../../../service/Api";

type HeatmapRow = {
  capacity: string;
  [speedRange: string]: number | string;
};

interface HeatmapItem {
  capacity_range: string;
  speed_range: string;
  count: number;
}

interface HeatmapResponse {
  data: HeatmapItem[];
}

const CapacitySpeedHeatmap = () => {
  const [dataset, setDataset] = useState<HeatmapRow[]>([]);
  const [speedBands, setSpeedBands] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchHeatmap() {
      try {
        const result = await apiService.get<HeatmapResponse>(
          "/api/venues/dashboard/capacity-speed-heatmap/"
        );

        const items: HeatmapItem[] = result.data?.data ?? [];

        if (!items.length) return;

        // Unique speed bands for legend and stacked bars
        const bands = Array.from(new Set(items.map((i) => i.speed_range)));
        setSpeedBands(bands);

        // Group data by capacity
        const grouped: Record<string, HeatmapRow> = {};

        items.forEach((item) => {
          if (!grouped[item.capacity_range]) {
            grouped[item.capacity_range] = { capacity: item.capacity_range };
            bands.forEach((b) => (grouped[item.capacity_range][b] = 0));
          }
          grouped[item.capacity_range][item.speed_range] = item.count;
        });

        setDataset(
          Object.values(grouped).sort(
            (a, b) => parseInt(a.capacity) - parseInt(b.capacity)
          )
        );
      } catch (error) {
        console.error("Error fetching heatmap data:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchHeatmap();
  }, []);

  if (loading) return <Typography>Loading...</Typography>;
  if (!dataset.length) return <Typography>No data available</Typography>;

  return (
    <Box sx={{ backgroundColor: "#08065cff", padding: 2, borderRadius: 2 }}>
      <BarChart
        dataset={dataset}
        xAxis={[
          {
            dataKey: "capacity",
            scaleType: "band",
            label: "Capacity",
          },
        ]}
        yAxis={[
          {
            label: "Number of Venues",
            min: 0,
            max: Math.max(
              ...dataset.map((d) =>
                speedBands.reduce((sum, b) => sum + (d[b] as number), 0)
              ),
              10
            ),
          },
        ]}
        series={speedBands.map((band, index) => ({
          dataKey: band,
          label: band, // <- this will show exact speed range in legend
          stack: "speed",
          color: `hsl(${(index / speedBands.length) * 360}, 70%, 50%)`,
          showLabel: true, // show the count on top of each bar
        }))}
        height={400}
      />
    </Box>
  );
};

export default CapacitySpeedHeatmap;
